import { Link, useSearchParams } from 'react-router-dom';

import { Alert } from '@/shared/ui';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { useVerifyEmail } from '../api/use-auth.query';
import { toUserMessage } from '@platform/api-client';

/**
 * 이메일 인증 콜백 View.
 *
 * ── 진입 경로 ───────────────────────────────────────────────────
 *   {VITE_MAIN_FRONT_URL}/auth/verify-email?token=xxxx
 *   (백엔드 EmailVerificationService 에서 메일에 넣는 링크 형식)
 *
 * ── 동작 ───────────────────────────────────────────────────────
 *   - useVerifyEmail(token) 이 token 변경 시 1회 자동 호출 (queryKey 캐시 기반).
 *   - 서버는 idempotent 하지 않음(usedAt 1회 처리). StrictMode 이중 마운트는
 *     queryClient 캐시가 흡수하므로 실제 네트워크는 한 번만 발생.
 *   - retry/refetch 가 모두 꺼져 있어 실패 시 자동 재시도 없음.
 */
export function VerifyEmailView() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const { isPending, isSuccess, isError, error } = useVerifyEmail(token);
  // useQuery 는 enabled=false 일 때도 isPending=true 이므로 token 유무를 함께 판단
  const isVerifying = !!token && isPending;

  return (
    <div className="w-full max-w-md mx-auto py-10 text-center">
      <h1 className="text-2xl font-bold text-adobe-dark mb-4">이메일 인증</h1>

      {!token && (
        <Alert tone="error">
          유효하지 않은 인증 링크입니다. 메일에 포함된 링크를 다시 확인해주세요.
        </Alert>
      )}

      {isVerifying && (
        <p className="text-adobe-muted">인증을 처리하고 있습니다...</p>
      )}

      {isSuccess && (
        <>
          <Alert tone="success">이메일 인증이 완료되었습니다.</Alert>
          <Link
            to={PATHS.LOGIN}
            className="inline-block mt-6 px-6 py-2 bg-adobe-red text-white font-bold rounded"
          >
            로그인 페이지로 이동
          </Link>
        </>
      )}

      {isError && (
        <>
          <Alert tone="error">{toUserMessage(error)}</Alert>
          <p className="mt-4 text-sm text-adobe-muted">
            링크가 만료되었거나 이미 사용된 경우 로그인 후 재발송할 수 있습니다.
          </p>
          <Link
            to={PATHS.LOGIN}
            className="inline-block mt-6 text-primary hover:underline"
          >
            로그인 페이지로 이동
          </Link>
        </>
      )}
    </div>
  );
}
